import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useContactInfo } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import ParticleField from "./ParticleField";
import LiveStatus from "./LiveStatus";
import { submitContact } from "@/lib/api";

const empty = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export default function Contact() {
  const { t, lang } = useLang();
  const CONTACT_INFO = useContactInfo();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const f = t.contact.fields;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const sendWhatsApp = () => {
    const f = t.contact.fields;
    const lines = [
      `${f.name}: ${form.name}`,
      `${f.phone}: ${form.phone}`,
      form.company && `${f.company}: ${form.company}`,
      form.service && `${f.service}: ${form.service}`,
      form.message && `${f.message}: ${form.message}`,
    ].filter(Boolean).join("\n");
    const msg = encodeURIComponent(`${t.cta.quote}\n${lines}`);
    window.open(`https://wa.me/${CONTACT_INFO.whatsapp}?text=${msg}`, "_blank");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) return;
    setLoading(true);
    try {
      await submitContact({ ...form, type: "quote", lang });
      toast.success(t.contact.success);
      setForm(empty);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full rounded-xl bg-[#0d1836] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[color:var(--primary)] transition-colors";

  const info = [
    { icon: Phone, label: t.contact.info.phone, value: CONTACT_INFO.phone, href: `tel:${CONTACT_INFO.phone}` },
    { icon: Mail, label: t.contact.info.email, value: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}` },
    { icon: MapPin, label: t.contact.info.location, value: t.contact.info.locationVal, href: null },
  ];

  return (
    <section id="contact" className="relative section-pad overflow-hidden" data-testid="contact-section">
      <ParticleField className="opacity-40" density={40} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 100%, rgba(0,242,96,0.08), transparent 60%)" }} />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Info side */}
          <div>
            <Reveal><SectionTag>{t.contact.tag}</SectionTag></Reveal>
            <Reveal delay={0.05}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 mb-4">{t.contact.title}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-slate-400 text-base sm:text-lg leading-relaxed mb-4">{t.contact.subtitle}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mb-8"><LiveStatus /></div>
            </Reveal>
            <div className="space-y-4">
              {info.map((item, i) => (
                <Reveal key={i} delay={0.1 + i * 0.08}>
                  <a href={item.href || undefined} className="flex items-center gap-4 card-surface rounded-2xl p-5 group" data-testid={`contact-info-${i}`}>
                    <div className="h-12 w-12 rounded-2xl grid place-items-center shrink-0" style={{ background: "rgba(0,242,96,0.12)" }}>
                      <item.icon size={22} className="text-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-0.5">{item.label}</div>
                      <div className="font-semibold" dir="ltr" style={{ textAlign: "start" }}>{item.value}</div>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Form side */}
          <Reveal delay={0.15}>
            <form onSubmit={onSubmit} className="card-surface rounded-3xl p-7 sm:p-9 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{f.name} *</label>
                  <input name="name" value={form.name} onChange={onChange} required className={inputClass} data-testid="contact-name" />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{f.phone} *</label>
                  <input name="phone" value={form.phone} onChange={onChange} required dir="ltr" className={inputClass} data-testid="contact-phone" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{f.email}</label>
                  <input name="email" type="email" value={form.email} onChange={onChange} dir="ltr" className={inputClass} data-testid="contact-email" />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{f.company}</label>
                  <input name="company" value={form.company} onChange={onChange} className={inputClass} data-testid="contact-company" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">{f.service}</label>
                <select name="service" value={form.service} onChange={onChange} className={inputClass} data-testid="contact-service">
                  <option value="">{t.contact.selectService}</option>
                  {t.services.items.map((s, i) => (
                    <option key={i} value={s.t} className="bg-[#0d1836]">{s.t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">{f.message} *</label>
                <textarea name="message" value={form.message} onChange={onChange} required rows={4} className={`${inputClass} resize-none`} data-testid="contact-message" />
              </div>
              <motion.button
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold text-[#04120a] disabled:opacity-60 transition-transform hover:scale-[1.01]"
                style={{ background: "var(--primary)" }}
                data-testid="contact-submit"
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> {t.contact.sending}</> : <><Send size={18} /> {t.contact.submit}</>}
              </motion.button>
              <button
                type="button"
                onClick={sendWhatsApp}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-bold border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 transition-colors"
                data-testid="contact-whatsapp"
              >
                <MessageCircle size={18} /> {t.whatsapp}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
